// src/components/instructor/LessonCloseoutModal.js
//
// Pass 84 — the ONE-MINUTE post-lesson closeout surface. A compact, centered dialog (the repo's idiomatic modal
// pattern — there is no drawer primitive) launched from the instructor's schedule lesson card. It reuses the
// canonical lessonInstance writers via `saveLessonCloseout`; it creates NO new collection.
//
// Fast path (target < 60s): (1) explicit "this lesson took place" acknowledgement, (2) a typed OR dictated human
// note, (3) Save. Outcome + next-focus are optional and collapsed. Human evidence is primary and saves
// independently of any AI; there is no AI call in this fast path (AI enrichment stays in the full Lesson Builder
// copilot), so AI availability/failure can never block or lose a valid closeout.

import React, { useState, useMemo, useCallback } from 'react';
import VoiceCaptureButton from '../ai/copilot/VoiceCaptureButton';
import { saveLessonCloseout } from '../../utils/lessonCloseoutWriter';
import {
  CLOSEOUT_OUTCOME_OPTIONS, evaluateCloseout, deriveCloseoutState, CLOSEOUT_STATE,
} from '../../utils/lessonCloseout';
import { inferQuickOutcomeChoice } from '../../utils/quickLessonLog';

const C = {
  card: '#FFFFFF', card2: '#F5F4F0', border: 'rgba(0,0,0,0.07)',
  blue: '#2563EB', green: '#16A34A', greenDim: 'rgba(22,163,74,0.09)',
  amber: '#D97706', amberDim: 'rgba(217,119,6,0.10)', red: '#DC2626',
  t1: '#1C1B18', t2: '#57564F', t3: '#8A8980',
};
const font = "'DM Sans',system-ui,sans-serif";

const OUTCOME_LABELS = {
  not_ready_yet: 'Needs more time',
  in_progress: 'In progress',
  strong_progress: 'Strong progress',
  achieved: 'Achieved goal',
};

function LessonCloseoutModal({ booking, lesson = null, ctx = {}, db, onClose, onSaved }) {
  // Reopen prefill: if the lesson is already delivered, treat occurrence as acknowledged and prefill the human
  // note + outcome so an edit shows the saved state (never a blank form that could erase evidence on save).
  const delivered = deriveCloseoutState(lesson) !== CLOSEOUT_STATE.NONE && deriveCloseoutState(lesson) !== CLOSEOUT_STATE.DELIVERY_UNCONFIRMED;
  const [occurred, setOccurred] = useState(delivered);
  const [note, setNote] = useState(lesson?.lessonOutcome?.instructorSummary || '');
  // Pass 84.1 — the optional outcome is NOT inferred into a writable value. `outcomeChoice === null` means "not
  // explicitly edited" → the existing lesson outcome is preserved on save. The existing outcome is only used to
  // HIGHLIGHT the current selection for display; it is never written unless the instructor clicks a chip.
  const inferredOutcome = useMemo(() => (lesson ? inferQuickOutcomeChoice(lesson) : 'in_progress'), [lesson]);
  const [outcomeChoice, setOutcomeChoice] = useState(null);
  const outcomeEdited = outcomeChoice !== null;
  const displayedOutcome = outcomeEdited ? outcomeChoice : inferredOutcome;
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // A completed closeout requires the explicit acknowledgement AND a non-empty human note (outcome is optional).
  const ready = useMemo(() => evaluateCloseout({ occurred, note }).ok, [occurred, note]);

  const appendTranscript = useCallback((text) => {
    setNote((prev) => (prev ? `${prev} ${text}` : text));
  }, []);

  const save = useCallback(async () => {
    if (saving || !ready) return;
    setSaving(true); setError('');
    try {
      const res = await saveLessonCloseout(db, {
        booking, occurred, note, outcomeChoice, explicitOutcome: outcomeEdited, status: 'completed', ...ctx,
      });
      setDone(true);
      if (onSaved) onSaved(res.lessonInstanceId);
    } catch (e) {
      setError(e?.message || 'Could not save the closeout.');
    } finally {
      setSaving(false);
    }
  }, [saving, ready, db, booking, occurred, note, outcomeChoice, outcomeEdited, ctx, onSaved]);

  const guest = booking?.requesterName || 'this lesson';

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="closeout-title" data-testid="closeout-modal"
      style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(17,17,17,0.42)', padding: 16, fontFamily: font }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
    >
      <div style={{ width: '100%', maxWidth: 460, background: C.card, borderRadius: 16, boxShadow: '0 12px 40px rgba(0,0,0,0.22)', padding: 22, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 id="closeout-title" style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.t1 }}>Close out lesson</h2>
          <div style={{ flex: 1 }} />
          <button type="button" aria-label="Close" data-testid="closeout-cancel" onClick={() => !saving && onClose?.()} style={{ border: 'none', background: 'none', fontSize: 22, lineHeight: 1, color: C.t3, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ fontSize: 13.5, color: C.t2, marginTop: 4 }}>{guest}</div>

        {done ? (
          <div data-testid="closeout-done" style={{ marginTop: 18, background: C.greenDim, color: C.green, borderRadius: 12, padding: '14px 16px', fontSize: 14, fontWeight: 700 }}>
            Closeout saved. This lesson is recorded as delivered.
            <div style={{ marginTop: 12 }}>
              <button type="button" onClick={() => onClose?.()} style={{ padding: '8px 16px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        ) : (
          <>
            {/* (1) Explicit occurrence acknowledgement — a real user action, never a form default side effect. */}
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16, cursor: 'pointer' }}>
              <input type="checkbox" data-testid="closeout-occurred" checked={occurred} onChange={(e) => setOccurred(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
              <span style={{ fontSize: 14.5, fontWeight: 700, color: C.t1 }}>This lesson took place</span>
            </label>
            <div style={{ fontSize: 12, color: C.t3, marginLeft: 28, marginTop: 2 }}>A lesson is only recorded as delivered when you confirm it happened — never from the calendar or booking status.</div>

            {/* (2) Human note — typed or dictated (browser speech-to-text). This is the primary coaching evidence. */}
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label htmlFor="closeout-note" style={{ fontSize: 13, fontWeight: 700, color: C.t2 }}>What happened in the lesson?</label>
                <div style={{ flex: 1 }} />
                <VoiceCaptureButton onTranscript={appendTranscript} disabled={saving} />
              </div>
              <textarea
                id="closeout-note" data-testid="closeout-note" value={note} onChange={(e) => setNote(e.target.value)}
                rows={4} placeholder="A sentence or two — what you worked on, how it went, next focus…"
                style={{ width: '100%', marginTop: 6, borderRadius: 10, border: `1px solid ${C.border}`, padding: '10px 12px', fontSize: 14, fontFamily: font, color: C.t1, resize: 'vertical', boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: 11.5, color: C.t3, marginTop: 4 }}>Your words are saved as-is. AI tidy-up is optional and available in the full Lesson Builder — it never replaces what you wrote.</div>
            </div>

            {/* Optional, collapsed enrichment — a lightweight outcome. Never required for the fast path. */}
            <button type="button" data-testid="closeout-more-toggle" onClick={() => setShowMore((v) => !v)} style={{ marginTop: 14, border: 'none', background: 'none', color: C.blue, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
              {showMore ? '– Hide outcome' : '+ Add an outcome (optional)'}
            </button>
            {showMore && (
              <div data-testid="closeout-outcome" style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {CLOSEOUT_OUTCOME_OPTIONS.map((opt) => (
                    <button
                      key={opt} type="button" data-testid={`closeout-outcome-${opt}`} onClick={() => setOutcomeChoice(opt)}
                      style={{ padding: '7px 12px', borderRadius: 20, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                        border: `1px solid ${displayedOutcome === opt ? C.blue : C.border}`,
                        background: displayedOutcome === opt ? C.blue : C.card2, color: displayedOutcome === opt ? '#fff' : C.t2 }}
                    >{OUTCOME_LABELS[opt] || opt}</button>
                  ))}
                </div>
                <div style={{ fontSize: 11.5, color: C.t3, marginTop: 6 }}>Optional — the outcome is only saved if you pick one here; leaving it untouched keeps the existing lesson outcome.</div>
              </div>
            )}

            {error ? <div role="alert" data-testid="closeout-error" style={{ marginTop: 14, color: C.red, fontSize: 13, fontWeight: 600 }}>{error}</div> : null}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" data-testid="closeout-save" disabled={!ready || saving} onClick={save}
                style={{ flex: 1, padding: '11px 18px', borderRadius: 11, border: 'none', fontSize: 15, fontWeight: 800, cursor: (!ready || saving) ? 'default' : 'pointer',
                  background: (!ready || saving) ? '#93B4F5' : C.blue, color: '#fff' }}>
                {saving ? 'Saving…' : 'Save closeout'}
              </button>
              <button type="button" onClick={() => !saving && onClose?.()} style={{ padding: '11px 16px', borderRadius: 11, border: `1px solid ${C.border}`, background: C.card, color: C.t2, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LessonCloseoutModal;
